import React, { useState, useCallback, useEffect } from 'react';
import { GridLayout } from './grid-layout';
import { SeatLayout } from './seat-layout';
import './buses-component-form.scss';
import { Button, Dropdown, Menu, Space, message } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { isValid } from '../../utils/utilities';
import { updateAdjacentSeatNumbers } from './buses-component-module';
// Define grid IDs as constants
const gridIds = [1, 2];

export const BusesSeatLayoutRoot = ({ seatGroups, seatLayoutData, resetTrigger, submitSeatLayoutChange }) => {
    const [grids, setGrids] = useState({
        [gridIds[0]]: {
            dimensions: { rows: 4, cols: 4 },
            seatTypes: {},
            seatNumbers: {},
            adjacentSeatNumbers: {},
            seatOrientations: {},
            assignedSeatGroups: {},
            seatFeatures: {},
            seatFeatureSizes: {},
            seatFeatureTypes: {},
            selectedSeats: [],
            editingSeat: null,
            numberConfig: {
                prefix: '',
                suffix: '',
                startNumber: '1'
            }
        }
    });

    const [activeGrid, setActiveGrid] = useState(gridIds[0]);
    const [hoveredCell, setHoveredCell] = useState(null);
    const [selectedTable, setSelectedTable] = useState(null);
    const [showSeatLayoutByGrid, setShowSeatLayoutByGrid] = useState({});
    const [isOldLayout, setIsOldLayout] = useState(false);

    // Effect to handle reset
    useEffect(() => {
        if (resetTrigger) {
            setGrids({
                [gridIds[0]]: {
                    dimensions: { rows: 4, cols: 4 },
                    seatTypes: {},
                    seatNumbers: {},
                    adjacentSeatNumbers: {},
                    seatOrientations: {},
                    assignedSeatGroups: {},
                    seatFeatures: {},
                    seatFeatureSizes: {},
                    seatFeatureTypes: {},
                    selectedSeats: [],
                    editingSeat: null,
                    numberConfig: {
                        prefix: '',
                        suffix: '',
                        startNumber: '1'
                    }
                }
            });
            setActiveGrid(gridIds[0]);
            setHoveredCell(null);
            setSelectedTable(null);
            setShowSeatLayoutByGrid({});
            setIsOldLayout(false);
        }
    }, [resetTrigger]);

    // Function to regenerate adjacent seat numbers
    const regenerateAdjacentSeatNumbers = useCallback(() => {
        setGrids(prevGrids => {
            const newGrids = { ...prevGrids };
            
            Object.entries(newGrids).forEach(([gridId, grid]) => {
                const newAdjacentSeatNumbers = updateAdjacentSeatNumbers(
                    grid.dimensions,
                    grid.seatTypes,
                    grid.seatNumbers,
                    grid.seatOrientations
                );

                newGrids[gridId] = {
                    ...grid,
                    adjacentSeatNumbers: newAdjacentSeatNumbers
                };
            });

            return newGrids;
        });
    }, []);

    // Effect to handle seatLayoutData
    useEffect(() => {
        if (seatLayoutData && Array.isArray(seatLayoutData) && seatLayoutData.length > 0) {
            setIsOldLayout(true);
            // Group seats by upper_deck
            const lowerDeckSeats = seatLayoutData.filter(seat => seat.upper_deck === 0);
            const upperDeckSeats = seatLayoutData.filter(seat => seat.upper_deck === 1);

            // Calculate dimensions for each deck
            const getDimensions = (seats) => {
                if (seats.length === 0) return { rows: 4, cols: 4 };
                const maxRow = Math.max(...seats.map(seat => seat.row));
                const maxCol = Math.max(...seats.map(seat => seat.column));
                return { rows: maxRow, cols: maxCol };
            };

            // Process seats for a deck
            const processDeckSeats = (seats, gridId) => {
                const dimensions = getDimensions(seats);
                const seatTypes = {};
                const seatNumbers = {};
                const assignedSeatGroups = {};
                const adjacentSeatNumbers = {};
                const seatOrientations = {};
                const seatFeatures = {};
                const seatFeatureSizes = {};
                const seatFeatureTypes = {};

                // First, mark all positions as empty
                for (let row = 0; row < dimensions.rows; row++) {
                    for (let col = 0; col < dimensions.cols; col++) {
                        const key = `${row}-${col}`;
                        seatTypes[key] = 'empty';
                    }
                }

                // Then, update with actual seat data
                seats.forEach(seat => {
                    const key = `${seat.row - 1}-${seat.column - 1}`;
                    let seatType = seat.seat_type;
                    
                    // Handle seat type the same way as in handleVerify
                    if (!isValid(seatType)) {
                        seatType = 'seater';
                    }
                    else if (seatType === 'empty') {
                        seatType = 'empty';
                    }

                    seatTypes[key] = seatType;
                    seatNumbers[key] = seat.seat_number;
                    adjacentSeatNumbers[key] = seat.adjacent_seat_number;
                    seatOrientations[key] = seat.seat_orientation;
                    if (seat.seat_group) {
                        assignedSeatGroups[key] = seat.seat_group;
                    }
                    if (seat.seat_feature) {
                        seatFeatures[key] = seat.seat_feature;
                        if (seat.seat_feature === 'Toilet') {
                            seatFeatureSizes[key] = seat.seat_feature_size || 'seater';
                        }
                        if (seat.seat_feature === 'Emergency Exit' && seat.feature_type) {
                            seatFeatureTypes[key] = seat.feature_type;
                        }
                    }
                    // Set default orientation for sleeper seats
                    // if (seatType === 'sleeper') {
                    //     seatOrientations[key] = 'portrait';
                    // }
                });

                return {
                    dimensions,
                    seatTypes,
                    seatNumbers,
                    assignedSeatGroups,
                    adjacentSeatNumbers,
                    seatOrientations,
                    seatFeatures,
                    seatFeatureSizes,
                    seatFeatureTypes,
                    selectedSeats: [],
                    editingSeat: null,
                    numberConfig: {
                        prefix: '',
                        suffix: '',
                        startNumber: '1'
                    }
                };
            };

            // Create new grids object
            const newGrids = {};
            
            // Add lower deck
            if (lowerDeckSeats.length > 0) {
                newGrids[gridIds[0]] = processDeckSeats(lowerDeckSeats, gridIds[0]);
                setShowSeatLayoutByGrid(prev => ({ ...prev, [gridIds[0]]: true }));
            }
            // Add upper deck if exists
            if (upperDeckSeats.length > 0) {
                newGrids[gridIds[1]] = processDeckSeats(upperDeckSeats, gridIds[1]);
                setShowSeatLayoutByGrid(prev => ({ ...prev, [gridIds[1]]: true }));
            }
            // Update state
            setGrids(newGrids);

            // Set initial active grid
            if (Object.keys(newGrids).length > 0) {
                setActiveGrid(parseInt(Object.keys(newGrids)[0]));
            }
        }
    }, [seatLayoutData, gridIds]);

    const handleGridAction = useCallback((action) => {
        if (Object.keys(grids).length >= 2 && action === 'new') {
            return;
        }

        if (action === 'copy') {
            const currentGrid = grids[gridIds[0]];
            const newGridId = gridIds[1];
            
            // Copy all grid data
            const newGrid = {
                dimensions: { ...currentGrid.dimensions },
                seatTypes: { ...currentGrid.seatTypes },
                seatNumbers: { ...currentGrid.seatNumbers },
                adjacentSeatNumbers: { ...currentGrid.adjacentSeatNumbers },
                assignedSeatGroups: { ...currentGrid.assignedSeatGroups },
                seatOrientations: { ...currentGrid.seatOrientations },
                seatFeatures: { ...currentGrid.seatFeatures },
                seatFeatureSizes: { ...currentGrid.seatFeatureSizes },
                seatFeatureTypes: { ...currentGrid.seatFeatureTypes },
                selectedSeats: [],
                editingSeat: null,
                numberConfig: { ...currentGrid.numberConfig }
            };

            // Update grids state
            setGrids(prev => ({
                ...prev,
                [newGridId]: newGrid
            }));

            // Copy seat layout visibility
            setShowSeatLayoutByGrid(prev => ({
                ...prev,
                [newGridId]: true // Set to true since we're copying an existing layout
            }));

            // Copy selected table dimensions
            if (selectedTable && selectedTable[gridIds[0]]) {
                setSelectedTable(prev => ({
                    ...prev,
                    [newGridId]: { ...prev[gridIds[0]] }
                }));
            }

            setActiveGrid(newGridId);
        } else if (action === 'new') {
            const newGridId = gridIds[1];
            const newGrid = {
                dimensions: { rows: 4, cols: 4 },
                seatTypes: {},
                seatNumbers: {},
                adjacentSeatNumbers: {},
                assignedSeatGroups: {},
                seatOrientations: {},
                seatFeatures: {},
                seatFeatureSizes: {},
                seatFeatureTypes: {},
                selectedSeats: [],
                editingSeat: null,
                numberConfig: {
                    prefix: '',
                    suffix: '',
                    startNumber: '1'
                }
            };
            setGrids(prev => ({
                ...prev,
                [newGridId]: newGrid
            }));
            setActiveGrid(newGridId);
        } else if (action === 'remove') {
            // Reset upper birth grid and seat layout
            setGrids(prev => {
                const newGrids = { ...prev };
                delete newGrids[gridIds[1]];
                return newGrids;
            });
            setActiveGrid(gridIds[0]);
            setShowSeatLayoutByGrid(prev => {
                const newShowSeatLayout = { ...prev };
                delete newShowSeatLayout[gridIds[1]];
                return newShowSeatLayout;
            });
            setSelectedTable(prev => {
                if (prev) {
                    const newSelectedTable = { ...prev };
                    delete newSelectedTable[gridIds[1]];
                    return newSelectedTable;
                }
                return prev;
            });
        }
    }, [grids, activeGrid, selectedTable]);

    const handleGridUpdate = useCallback((gridId, updates) => {
        setGrids(prevGrids => ({
            ...prevGrids,
            [gridId]: {
                ...prevGrids[gridId],
                ...updates
            }
        }));
    }, []);

    const handleCellHover = useCallback((row, col, gridId) => {
        setHoveredCell({ row, col, gridId });
    }, []);

    const handleCellClick = useCallback((rowIndex, colIndex, gridId) => {
        const newDimensions = { rows: rowIndex + 1, cols: colIndex + 1 };
        
        // Update only the active grid's dimensions
        setGrids(prev => ({
            ...prev,
            [gridId]: {
                ...prev[gridId],
                dimensions: newDimensions
            }
        }));

        // Update selectedTable only for the active grid
        let tableDimensions = {};
        if (isValid(selectedTable)) {
            tableDimensions = { ...selectedTable };
            tableDimensions[gridId] = newDimensions;
        }
        else {
            tableDimensions[gridId] = newDimensions;
        }
        setSelectedTable(tableDimensions);
        
        // Show seat layout when dimensions are selected
        setShowSeatLayoutByGrid(prev => ({ ...prev, [gridId]: true }));
    }, [activeGrid]);

    const handleMouseLeave = useCallback(() => {
        setHoveredCell(null);
    }, []);

    const handleGridSectionClick = useCallback((gridId, event) => {
        // Prevent event from bubbling up if clicking on the dropdown
        if (event.target.closest('.ant-dropdown-trigger')) {
            return;
        }
        setActiveGrid(parseInt(gridId));
    }, []);

    const handleVerify = useCallback(() => {
        const seatList = [];
        let extraSeatsInLastRow = false;
        const gridCounts = {}, seatGroupsAssigned = [];
        let totalSeatCount = 0, totalLevels = 0, totalSeatGroupsAssigned = 0;
        let lowerDeckRowColCount = { 'num_rows': 0, 'num_cols': 0 };
        let upperDeckRowColCount = { 'num_rows': 0, 'num_cols': 0 };
        let seatNumberList = [], adjacentSeatNumberList = [], duplicateSeatNumbers = [], duplicateAdjacentSeatNumbers = [];
        let unassignedSeatNumber = false;
        let exitsMissingFeatureType = 0;

        // Process each grid
        Object.entries(grids).forEach(([gridId, gridData]) => {
            const rowLength = gridData.dimensions.rows;
            const colLength = gridData.dimensions.cols;
            const isUpperDeck = parseInt(gridId) === gridIds[1];
            totalLevels++;
            
            // Initialize counts for this grid
            gridCounts[gridId] = {
                nonEmptySeatsInRow: new Array(rowLength).fill(0),
                nonEmptySeatsInCol: new Array(colLength).fill(0)
            };
            
            // Single pass to collect seat data and count non-empty seats
            for (let row = 0; row < rowLength; row++) {
                for (let col = 0; col < colLength; col++) {
                    const seatKey = `${row}-${col}`;
                    const seatType = gridData.seatTypes[seatKey] || 'seater';
                    const seatFeature = (gridData.seatFeatures && gridData.seatFeatures[seatKey]) || null;

                    const seatFeatureType = (gridData.seatFeatureTypes && gridData.seatFeatureTypes[seatKey]) || null;
                    if (seatFeature === 'Emergency Exit' && !seatFeatureType) {
                        exitsMissingFeatureType++;
                    }

                    if (seatType === 'empty' && seatFeature) {
                        // Featured empty cells (Toilet, Emergency exit gaps) are persisted
                        // as non-bookable 'Empty' rows so the position carries the feature.
                        // The feature's size (seater/sleeper footprint) rides on the sleeper flag.
                        const featureSize = (gridData.seatFeatureSizes && gridData.seatFeatureSizes[seatKey]) || 'seater';
                        seatList.push({
                            seat_number: 'Empty',
                            seat_row_number: row + 1,
                            seat_column_number: col + 1,
                            seat_group_id: null,
                            adjacent_seat_number: 'Empty',
                            seat_orientation: 0,
                            sleeper: featureSize === 'sleeper',
                            upper_deck: isUpperDeck ? 1 : 0,
                            window_seat: false,
                            seat_feature: seatFeature,
                            feature_type: seatFeature === 'Emergency Exit' ? seatFeatureType : null
                        });
                    }
                    else if (seatType !== 'empty') {
                        totalSeatCount++;
                        gridCounts[gridId].nonEmptySeatsInRow[row]++;
                        gridCounts[gridId].nonEmptySeatsInCol[col]++;
                        
                        const seatNumber = gridData.seatNumbers[seatKey] || '';
                        const seatGroup = gridData.assignedSeatGroups[seatKey] || null;
                        const adjacentSeatNumber = gridData.adjacentSeatNumbers[seatKey] || '';
                        const seatOrientation = gridData.seatOrientations[seatKey] === "landscape" ? 270 : 0;

                        if (seatGroup && !seatGroupsAssigned.includes(seatGroup)) {
                            seatGroupsAssigned.push(seatGroup);
                        }
                        if(seatGroup){
                            totalSeatGroupsAssigned++;
                        }
                        if(seatNumberList.includes(seatNumber)){
                            duplicateSeatNumbers.push(seatNumber);
                        }

                        if (adjacentSeatNumberList.includes(adjacentSeatNumber)) {
                            duplicateAdjacentSeatNumbers.push(adjacentSeatNumber);
                        }
                        seatNumberList.push(seatNumber);
                        
                        if (!seatNumber) {
                            unassignedSeatNumber = true;
                        }

                        adjacentSeatNumberList.push(adjacentSeatNumber);
                        
                        seatList.push({
                            seat_number: seatNumber,
                            seat_row_number: row + 1,
                            seat_column_number: col + 1,
                            seat_group_id: seatGroup,
                            adjacent_seat_number: adjacentSeatNumber,
                            seat_orientation: seatOrientation,
                            sleeper: seatType === "sleeper",
                            upper_deck: isUpperDeck ? 1 : 0,
                            window_seat: col === 0 || col === colLength - 1,
                            seat_feature: seatFeature,
                            feature_type: seatFeature === 'Emergency Exit' ? seatFeatureType : null
                        });

                        // Check for extra seats in last row only for lower deck
                        if (!isUpperDeck && row === rowLength - 1) {
                            let prevSeatKey = `${row-1}-${col}`;
                            if (!extraSeatsInLastRow && gridData.seatTypes[prevSeatKey] === "empty") {
                                extraSeatsInLastRow = true;
                            }
                        }
                    }
                }
            }

            // Count rows and columns with more than 1 non-empty seat
            if (isUpperDeck) {
                upperDeckRowColCount.num_rows = gridCounts[gridId].nonEmptySeatsInRow.filter(count => count > 0).length;
                upperDeckRowColCount.num_cols = gridCounts[gridId].nonEmptySeatsInCol.filter(count => count > 0).length;
            } else {
                lowerDeckRowColCount.num_rows = gridCounts[gridId].nonEmptySeatsInRow.filter(count => count > 0).length;
                lowerDeckRowColCount.num_cols = gridCounts[gridId].nonEmptySeatsInCol.filter(count => count > 0).length;
            }
        });

        if (unassignedSeatNumber) {
            message.error('Some seats have unassigned seat numbers');
        }
        else if (duplicateSeatNumbers.length > 0) {
            message.error(`Duplicate entry of seat numbers found: ${duplicateSeatNumbers.join(', ')}`);
        }
        else if (exitsMissingFeatureType > 0) {
            message.error('Select a Feature Type (Roof / Side / Rear) for every Emergency Exit');
        }
        else {
            submitSeatLayoutChange({
                grids,
                seatList, 
                extraSeatsInLastRow,
                lowerDeckRowColCount,
                upperDeckRowColCount,
                totalSeatCount,
                totalLevels,
                seatGroupsAssigned,
                isAllSeatGroupsAssigned: totalSeatGroupsAssigned == totalSeatCount
            });
        }
    }, [grids]);

    return (
        <div className="table-selector">
            <div className="grids-container">
                {Object.entries(grids).map(([gridId, grid]) => (
                    <div 
                        key={gridId} 
                        className={`grid-section ${activeGrid === parseInt(gridId) ? 'active' : ''}`}
                        onClick={(e) => handleGridSectionClick(parseInt(gridId), e)}
                    >
                        <div className="grid-header">
                            <h3>{parseInt(gridId) === 1 ? 'Lower Birth' : 'Upper Birth'}</h3>
                            {activeGrid === parseInt(gridId) && (
                                <Dropdown 
                                    overlay={
                                        <Menu onClick={(e) => e.domEvent.stopPropagation()}>
                                            {parseInt(gridId) === 1 ? (
                                                <>
                                                    {!isOldLayout && <Menu.Item 
                                                        key="copy" 
                                                        onClick={(e) => {
                                                            e.domEvent.stopPropagation();
                                                            handleGridAction('copy');
                                                        }}
                                                        disabled={Object.keys(grids).length >= 2}
                                                    >
                                                        Copy Lower Birth
                                                    </Menu.Item>}
                                                    <Menu.Item 
                                                        key="new" 
                                                        onClick={(e) => {
                                                            e.domEvent.stopPropagation();
                                                            handleGridAction('new');
                                                        }}
                                                        disabled={Object.keys(grids).length >= 2}
                                                    >
                                                        New Upper Birth
                                                    </Menu.Item>
                                                </>
                                            ) : (
                                                <Menu.Item 
                                                    key="remove" 
                                                    onClick={(e) => {
                                                        e.domEvent.stopPropagation();
                                                        handleGridAction('remove');
                                                    }}
                                                >
                                                    Remove Upper Birth
                                                </Menu.Item>
                                            )}
                                        </Menu>
                                    } 
                                    trigger={['click']}
                                >
                                    <Button 
                                        icon={parseInt(gridId) === 1 ? <PlusOutlined /> : <MinusOutlined />} 
                                        size="small"
                                        disabled={Object.keys(grids).length >= 2 && parseInt(gridId) === 1}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {parseInt(gridId) === 1 ? 'Add Upper Birth' : 'Remove Upper Birth'}
                                    </Button>
                                </Dropdown>
                            )}
                        </div>
                        <GridLayout
                            grid={grid}
                            gridId={parseInt(gridId)}
                            hoveredCell={hoveredCell}
                            selectedTable={selectedTable}
                            onHover={handleCellHover}
                            onClick={handleCellClick}
                            onMouseLeave={handleMouseLeave}
                        />
                        {showSeatLayoutByGrid[parseInt(gridId)] && grid.dimensions.rows > 0 && grid.dimensions.cols > 0 && (
                            <SeatLayout
                                grid={grid}
                                seatGroups={seatGroups}
                                onUpdate={(updates) => handleGridUpdate(parseInt(gridId), updates)}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className="verify-button-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                <Space>
                    <Button
                        type="primary"
                        onClick={handleVerify}
                        disabled={!Object.values(grids).some(grid => grid.dimensions.rows > 0 && grid.dimensions.cols > 0)}
                    >
                        Set Seat Layout
                    </Button>
                    {/* <Button
                        onClick={regenerateAdjacentSeatNumbers}
                        disabled={!Object.values(grids).some(grid => grid.dimensions.rows > 0 && grid.dimensions.cols > 0)}
                    >
                        Regenerate Adjacent Numbers
                    </Button> */}
                </Space>
            </div>
        </div>
    );
}; 